import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	Search,
	Package,
	Shield,
	ArrowRight,
	ChevronDown,
	ChevronRight,
	ExternalLink,
	Pencil,
	Check,
	X,
	Trash2,
	Loader2,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/toaster";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { DependencyEditor } from "@/components/features/DependencyEditor";
import { EditFeatureDialog } from "@/components/features/EditFeatureDialog";
import { ValidateAllDialog } from "@/components/features/ValidateAllDialog";
import { CreateFeatureDialog } from "@/components/features/CreateFeatureDialog";
import * as api from "@/services/api";
import type { Feature, FeatureDependency } from "@/types";

// Action to color mapping
const actionColors: Record<string, string> = {
	view: "bg-blue-100 text-blue-800",
	create: "bg-green-100 text-green-800",
	update: "bg-yellow-100 text-yellow-800",
	delete: "bg-red-100 text-red-800",
	approve: "bg-purple-100 text-purple-800",
	export: "bg-gray-100 text-gray-800",
	manage: "bg-indigo-100 text-indigo-800",
};

// Module to color mapping
const moduleColors: Record<string, string> = {
	core: "bg-slate-100 text-slate-800",
	organization: "bg-blue-100 text-blue-800",
	hris: "bg-green-100 text-green-800",
	ams: "bg-yellow-100 text-yellow-800",
	monitoring: "bg-orange-100 text-orange-800",
	kpi: "bg-pink-100 text-pink-800",
	admin: "bg-red-100 text-red-800",
	subscription: "bg-cyan-100 text-cyan-800",
	notifications: "bg-amber-100 text-amber-800",
	gamification: "bg-violet-100 text-violet-800",
	appraisal: "bg-teal-100 text-teal-800",
};

export function PermissionRegistry() {
	const [search, setSearch] = useState("");
	const [expandedFeature, setExpandedFeature] = useState<string | null>(null);
	const [selectedModule, setSelectedModule] = useState<string | null>(null);
	const [editingFeature, setEditingFeature] = useState<string | null>(null);
	const [editingScopeType, setEditingScopeType] = useState<string | null>(null);
	const [pendingScopeType, setPendingScopeType] = useState<
		"collective" | "personal" | "administrative" | null
	>(null);
	const [editDialogFeature, setEditDialogFeature] = useState<Feature | null>(null);

	const queryClient = useQueryClient();

	const { data: features, isLoading } = useQuery({
		queryKey: ["features"],
		queryFn: api.getFeatures,
	});

	const { data: dependencies } = useQuery({
		queryKey: ["dependencies"],
		queryFn: api.getAllDependencies,
	});

	const { data: planMatrix } = useQuery({
		queryKey: ["plan-matrix"],
		queryFn: api.getPlanFeatureMatrix,
	});

	// Mutation for updating dependencies
	const updateDependenciesMutation = useMutation({
		mutationFn: async ({
			featureCode,
			deps,
		}: {
			featureCode: string;
			deps: FeatureDependency[];
		}) => {
			return api.updateFeatureDependencies(
				featureCode,
				deps.map((d) => ({
					feature: d.feature,
					action: d.action,
					reason: d.reason,
				})),
			);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["features"] });
			queryClient.invalidateQueries({ queryKey: ["dependencies"] });
			toast({
				title: "Dependencies updated",
				description: "Feature dependencies have been saved successfully.",
				variant: "success",
			});
			setEditingFeature(null);
		},
		onError: (error: Error) => {
			toast({
				title: "Failed to update dependencies",
				description: error.message,
				variant: "destructive",
			});
		},
	});

	// Mutation for updating scope type
	const updateScopeTypeMutation = useMutation({
		mutationFn: async ({
			featureCode,
			scopeType,
		}: {
			featureCode: string;
			scopeType: "collective" | "personal" | "administrative";
		}) => {
			return api.updateFeatureScopeType(featureCode, scopeType);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["features"] });
			toast({
				title: "Scope type updated",
				description: "Feature scope type has been saved successfully.",
				variant: "success",
			});
			setEditingScopeType(null);
			setPendingScopeType(null);
		},
		onError: (error: Error) => {
			toast({
				title: "Failed to update scope type",
				description: error.message,
				variant: "destructive",
			});
		},
	});

	// Mutation for deleting a feature
	const deleteFeatureMutation = useMutation({
		mutationFn: api.deleteFeature,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["features"] });
			queryClient.invalidateQueries({ queryKey: ["dependencies"] });
			queryClient.invalidateQueries({ queryKey: ["plan-matrix"] });
			toast({
				title: "Feature deleted",
				description: "Feature has been deleted successfully.",
				variant: "success",
			});
		},
		onError: (error: Error) => {
			toast({
				title: "Failed to delete feature",
				description: error.message,
				variant: "destructive",
			});
		},
	});

	const handleDeleteFeature = (e: React.MouseEvent, feature: Feature) => {
		e.stopPropagation();
		if (window.confirm(`Are you sure you want to delete "${feature.name || feature.slug}"? This action cannot be undone.`)) {
			deleteFeatureMutation.mutate(feature.slug);
		}
	};

	// Group features by module
	const groupedFeatures = useMemo(() => {
		if (!features) return {};
		return features.reduce(
			(acc, feature) => {
				const module = feature.module || "other";
				if (!acc[module]) acc[module] = [];
				acc[module].push(feature);
				return acc;
			},
			{} as Record<string, Feature[]>,
		);
	}, [features]);

	// Get unique modules
	const modules = useMemo(
		() => Object.keys(groupedFeatures).sort(),
		[groupedFeatures],
	);

	// Filter features
	const filteredFeatures = useMemo(() => {
		if (!features) return [];
		return features.filter((f) => {
			const matchesSearch =
				!search ||
				f.slug.toLowerCase().includes(search.toLowerCase()) ||
				f.ui?.label?.toLowerCase().includes(search.toLowerCase());
			const matchesModule = !selectedModule || f.module === selectedModule;
			return matchesSearch && matchesModule;
		});
	}, [features, search, selectedModule]);

	// Get dependencies for a feature
	const getDependencies = (featureSlug: string) => {
		return dependencies?.dependencyMap?.[featureSlug] || [];
	};

	// Get dependents (what depends on this feature)
	const getDependents = (featureSlug: string) => {
		return dependencies?.reverseDependencyMap?.[featureSlug] || [];
	};

	// Get plans that include this feature
	const getPlansForFeature = (featureSlug: string) => {
		if (!planMatrix) return [];
		return planMatrix.plans.filter(
			(plan) => planMatrix.matrix[plan.name]?.[featureSlug],
		);
	};

	// Get feature dependencies as FeatureDependency[]
	const getFeatureDependencies = (feature: Feature): FeatureDependency[] => {
		return feature.requires || [];
	};

	// Handle edit click
	const handleEditClick = (e: React.MouseEvent, featureSlug: string) => {
		e.stopPropagation();
		if (editingFeature === featureSlug) {
			setEditingFeature(null);
		} else {
			setEditingFeature(featureSlug);
			setExpandedFeature(featureSlug);
		}
	};

	// Handle save dependencies
	const handleSaveDependencies = async (
		featureCode: string,
		deps: FeatureDependency[],
	) => {
		await updateDependenciesMutation.mutateAsync({
			featureCode,
			deps,
		});
	};

	// Handle cancel edit
	const handleCancelEdit = () => {
		setEditingFeature(null);
	};

	// Handle edit scope type click
	const handleEditScopeTypeClick = (
		e: React.MouseEvent,
		feature: Feature,
	) => {
		e.stopPropagation();
		setEditingScopeType(feature.slug);
		setPendingScopeType(feature.scopeType || null);
	};

	// Handle save scope type
	const handleSaveScopeType = async (featureCode: string) => {
		if (!pendingScopeType) return;
		await updateScopeTypeMutation.mutateAsync({
			featureCode,
			scopeType: pendingScopeType,
		});
	};

	// Handle cancel scope type edit
	const handleCancelScopeTypeEdit = () => {
		setEditingScopeType(null);
		setPendingScopeType(null);
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-3xl font-bold text-gray-900">
						Permission Registry
					</h1>
					<p className="text-gray-600 mt-2">
						Browse all features, their actions, dependencies, and package
						assignments
					</p>
				</div>
				<div className="flex items-center gap-2">
					<ValidateAllDialog />
					<CreateFeatureDialog existingModules={modules} />
				</div>
			</div>

			{/* Filters */}
			<div className="flex gap-4 flex-wrap">
				<div className="relative flex-1 min-w-[300px]">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
					<Input
						placeholder="Search features..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="pl-10"
					/>
				</div>
				<div className="flex gap-2 flex-wrap">
					<Button
						variant={selectedModule === null ? "default" : "outline"}
						size="sm"
						onClick={() => setSelectedModule(null)}
					>
						All
					</Button>
					{modules.map((module) => (
						<Button
							key={module}
							variant={selectedModule === module ? "default" : "outline"}
							size="sm"
							onClick={() => setSelectedModule(module)}
						>
							{module}
						</Button>
					))}
				</div>
			</div>

			{/* Stats */}
			<div className="grid grid-cols-4 gap-4">
				<Card>
					<CardContent className="pt-4">
						<div className="text-2xl font-bold">{features?.length || 0}</div>
						<p className="text-sm text-muted-foreground">Total Features</p>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-4">
						<div className="text-2xl font-bold">{modules.length}</div>
						<p className="text-sm text-muted-foreground">Modules</p>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-4">
						<div className="text-2xl font-bold">
							{Object.keys(dependencies?.dependencyMap || {}).length}
						</div>
						<p className="text-sm text-muted-foreground">With Dependencies</p>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-4">
						<div className="text-2xl font-bold">
							{planMatrix?.plans.length || 0}
						</div>
						<p className="text-sm text-muted-foreground">Plans</p>
					</CardContent>
				</Card>
			</div>

			{/* Feature List */}
			<div className="space-y-3">
				{filteredFeatures.map((feature) => {
					const isExpanded = expandedFeature === feature.slug;
					const isEditing = editingFeature === feature.slug;
					const deps = getDependencies(feature.slug);
					const dependents = getDependents(feature.slug);
					const plans = getPlansForFeature(feature.slug);

					return (
						<Card key={feature.slug} className="overflow-hidden">
							<div
								className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${isEditing ? "bg-blue-50/30" : ""}`}
								onClick={() =>
									setExpandedFeature(isExpanded ? null : feature.slug)
								}
							>
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-3">
										<Button variant="ghost" size="icon" className="h-6 w-6">
											{isExpanded ? (
												<ChevronDown className="h-4 w-4" />
											) : (
												<ChevronRight className="h-4 w-4" />
											)}
										</Button>
										<Package className="w-5 h-5 text-gray-400" />
										<div>
											<div className="flex items-center gap-2">
												<span className="font-medium">
													{feature.ui?.label || feature.slug}
												</span>
												<Badge
													variant="outline"
													className={moduleColors[feature.module] || ""}
												>
													{feature.module}
												</Badge>
												{feature.ui?.hidden && (
													<Badge variant="secondary">Hidden</Badge>
												)}
											</div>
											<p className="text-sm text-muted-foreground font-mono">
												{feature.slug}
											</p>
										</div>
									</div>
									<div className="flex items-center gap-4">
										{/* Actions */}
										<div className="flex gap-1">
											{feature.actions.map((action) => (
												<Badge
													key={action}
													className={`text-xs ${actionColors[action] || ""}`}
												>
													{action}
												</Badge>
											))}
										</div>
										{/* Dependency indicators */}
										{(deps.length > 0 || dependents.length > 0) && (
											<div className="flex items-center gap-2 text-xs text-muted-foreground">
												{deps.length > 0 && (
													<span className="flex items-center gap-1">
														<ArrowRight className="w-3 h-3" />
														{deps.length} deps
													</span>
												)}
												{dependents.length > 0 && (
													<span className="flex items-center gap-1">
														<Shield className="w-3 h-3" />
														{dependents.length} uses
													</span>
												)}
											</div>
										)}
									</div>
								</div>
							</div>

							{/* Expanded Details */}
							{isExpanded && (
								<div className="border-t bg-gray-50 p-4 space-y-4">
									{/* Edit Mode */}
									{isEditing ? (
										<DependencyEditor
											feature={feature}
											allFeatures={features || []}
											dependencyMap={dependencies?.dependencyMap || {}}
											initialDependencies={getFeatureDependencies(feature)}
											onSave={(deps) =>
												handleSaveDependencies(feature.slug, deps)
											}
											onCancel={handleCancelEdit}
											isSaving={updateDependenciesMutation.isPending}
										/>
									) : (
										<>
											{/* Edit / Delete Feature Buttons */}
											<div className="flex justify-end gap-2">
												<Button
													variant="outline"
													size="sm"
													onClick={(e) => {
														e.stopPropagation();
														setEditDialogFeature(feature);
													}}
												>
													<Pencil className="h-3 w-3 mr-1" />
													Edit Feature
												</Button>
												<Button
													variant="outline"
													size="sm"
													className="text-red-600 hover:text-red-700 hover:bg-red-50"
													onClick={(e) => handleDeleteFeature(e, feature)}
													disabled={deleteFeatureMutation.isPending}
												>
													{deleteFeatureMutation.isPending ? (
														<Loader2 className="h-3 w-3 mr-1 animate-spin" />
													) : (
														<Trash2 className="h-3 w-3 mr-1" />
													)}
													Delete
												</Button>
											</div>

											{/* Plans */}
											<div>
												<h4 className="text-sm font-medium mb-2">
													Available in Plans
												</h4>
												<div className="flex gap-2">
													{plans.length > 0 ? (
														plans.map((plan) => (
															<Badge
																key={plan.slug}
																variant="outline"
																style={{
																	borderColor: plan.color,
																	backgroundColor: `${plan.color}20`,
																}}
															>
																{plan.name}
															</Badge>
														))
													) : (
														<span className="text-sm text-muted-foreground">
															Not assigned to any plan
														</span>
													)}
												</div>
											</div>

											{/* Dependencies */}
											<div>
												<div className="flex items-center justify-between mb-2">
													<h4 className="text-sm font-medium">
														Requires (Dependencies)
													</h4>
													<Button
														variant="outline"
														size="sm"
														onClick={(e) => handleEditClick(e, feature.slug)}
													>
														<Pencil className="h-3 w-3 mr-1" />
														Edit Dependencies
													</Button>
												</div>
												{deps.length > 0 ? (
													<div className="flex flex-wrap gap-2">
														{getFeatureDependencies(feature).map((dep) => (
															<Badge
																key={`${dep.feature}:${dep.action}`}
																variant="outline"
																className="cursor-pointer hover:bg-blue-50"
																onClick={(e) => {
																	e.stopPropagation();
																	setExpandedFeature(dep.feature);
																}}
																title={dep.reason || undefined}
															>
																{dep.feature}:{dep.action}
																{dep.reason && (
																	<span className="ml-1 text-muted-foreground">
																		(*)
																	</span>
																)}
																<ExternalLink className="w-3 h-3 ml-1" />
															</Badge>
														))}
													</div>
												) : (
													<span className="text-sm text-muted-foreground">
														No dependencies
													</span>
												)}
											</div>

											{/* Dependents */}
											{dependents.length > 0 && (
												<div>
													<h4 className="text-sm font-medium mb-2">
														Used By (Dependents)
													</h4>
													<div className="flex flex-wrap gap-2">
														{dependents.map((dep) => (
															<Badge
																key={dep}
																variant="secondary"
																className="cursor-pointer hover:bg-gray-200"
																onClick={(e) => {
																	e.stopPropagation();
																	setExpandedFeature(dep);
																}}
															>
																{dep}
																<ExternalLink className="w-3 h-3 ml-1" />
															</Badge>
														))}
													</div>
													<p className="text-xs text-muted-foreground mt-1">
														Disabling this feature will break{" "}
														{dependents.length} other feature(s)
													</p>
												</div>
											)}

											{/* Scope Type */}
											<div>
												<div className="flex items-center justify-between mb-2">
													<h4 className="text-sm font-medium">Scope Type</h4>
													{editingScopeType !== feature.slug && (
														<Button
															variant="outline"
															size="sm"
															onClick={(e) =>
																handleEditScopeTypeClick(e, feature)
															}
														>
															<Pencil className="h-3 w-3 mr-1" />
															Edit Scope Type
														</Button>
													)}
												</div>
												{editingScopeType === feature.slug ? (
													<div className="flex items-center gap-2">
														<Select
															value={pendingScopeType || ""}
															onValueChange={(value) =>
																setPendingScopeType(
																	value as
																		| "collective"
																		| "personal"
																		| "administrative",
																)
															}
														>
															<SelectTrigger className="w-[200px]">
																<SelectValue placeholder="Select scope type" />
															</SelectTrigger>
															<SelectContent>
																<SelectItem value="personal">
																	Personal
																</SelectItem>
																<SelectItem value="collective">
																	Collective
																</SelectItem>
																<SelectItem value="administrative">
																	Administrative
																</SelectItem>
															</SelectContent>
														</Select>
														<Button
															variant="default"
															size="sm"
															onClick={() => handleSaveScopeType(feature.slug)}
															disabled={
																updateScopeTypeMutation.isPending ||
																!pendingScopeType
															}
														>
															<Check className="h-4 w-4" />
														</Button>
														<Button
															variant="outline"
															size="sm"
															onClick={handleCancelScopeTypeEdit}
															disabled={updateScopeTypeMutation.isPending}
														>
															<X className="h-4 w-4" />
														</Button>
													</div>
												) : feature.scopeType ? (
													<>
														<Badge
															variant={
																feature.scopeType === "administrative"
																	? "destructive"
																	: feature.scopeType === "collective"
																		? "default"
																		: "secondary"
															}
														>
															{feature.scopeType}
														</Badge>
														<p className="text-xs text-muted-foreground mt-1">
															{feature.scopeType === "administrative" &&
																"Requires organization-level access"}
															{feature.scopeType === "collective" &&
																"Works with org or department scope"}
															{feature.scopeType === "personal" &&
																"Can work with self scope"}
														</p>
													</>
												) : (
													<span className="text-sm text-muted-foreground">
														No scope type set
													</span>
												)}
											</div>
										</>
									)}
								</div>
							)}
						</Card>
					);
				})}
			</div>

			{filteredFeatures.length === 0 && (
				<div className="text-center py-12 text-muted-foreground">
					No features match your search
				</div>
			)}

			{/* Edit Feature Dialog */}
			{editDialogFeature && (
				<EditFeatureDialog
					feature={editDialogFeature}
					open={!!editDialogFeature}
					onOpenChange={(open) => {
						if (!open) setEditDialogFeature(null);
					}}
					existingModules={modules}
				/>
			)}
		</div>
	);
}
